import { useEffect, useState } from 'react';

/**
 * Per-user wishlist persistence layer.
 *
 * Until the backend exposes `wishlistService` (see
 * BACKEND_AGENT_PROMPTS Item 4) the wishlist is a per-user
 * `localStorage` slot at `kss.wishlist.v1:<userId>`. The shape is
 * deliberately minimal — just `productId` + `addedAt` — so that
 * when the service ships the local rows can be migrated to the
 * server without a schema rewrite.
 *
 * A lightweight `kss:wishlist-change` window event is emitted on
 * every write, and `useWishlist()` subscribes to it so two tiles
 * heart-toggling the same product update in lockstep without a
 * shared React provider.
 */

const STORAGE_PREFIX = 'kss.wishlist.v1';
const CHANGE_EVENT = 'kss:wishlist-change';

export interface WishlistEntry {
  productId: string;
  addedAt: string;
}

function storageKey(userId?: string | null): string {
  return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function readRaw(userId?: string | null): WishlistEntry[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as WishlistEntry[];
  } catch {
    /* malformed JSON — start clean */
  }
  return [];
}

function writeRaw(userId: string | null | undefined, entries: WishlistEntry[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(entries));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { userId } }));
  } catch {
    /* storage full / disabled — silent */
  }
}

/**
 * React hook returning `{ entries, has(productId), toggle(productId), remove(productId), clear() }`.
 * The list is reactive to `kss:wishlist-change` events emitted by every
 * mutation, plus `storage` events from other browser tabs.
 */
export function useWishlist(userId?: string | null) {
  const [entries, setEntries] = useState<WishlistEntry[]>(() => readRaw(userId));

  useEffect(() => {
    setEntries(readRaw(userId));
    const onChange = () => setEntries(readRaw(userId));
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(userId)) onChange();
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  const has = (productId: string): boolean =>
    entries.some((e) => e.productId === productId);

  const toggle = (productId: string): boolean => {
    const current = readRaw(userId);
    const idx = current.findIndex((e) => e.productId === productId);
    let next: WishlistEntry[];
    let added: boolean;
    if (idx >= 0) {
      next = [...current.slice(0, idx), ...current.slice(idx + 1)];
      added = false;
    } else {
      next = [{ productId, addedAt: new Date().toISOString() }, ...current];
      added = true;
    }
    writeRaw(userId, next);
    return added;
  };

  const remove = (productId: string) => {
    const current = readRaw(userId);
    writeRaw(
      userId,
      current.filter((e) => e.productId !== productId),
    );
  };

  const clear = () => writeRaw(userId, []);

  return { entries, has, toggle, remove, clear };
}
